// src/pages/tasks/TasksPage.tsx
import React, { useState, useEffect } from 'react';
import {
  CheckCircle,
  Clock,
  AlertTriangle,
  Plus,
  Filter,
  Calendar,
  User,
  X,
  Edit2,
  Repeat
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { taskService } from '../../services/task.service';
import { userService } from '../../services/user.service';
import { Task, TaskStatus, TaskPriority, TaskType, TaskStats } from '../../types/task.types';
import { User as UserType } from '../../types/user.types';
import { format } from 'date-fns';
import { toast } from 'react-hot-toast';
import { useActivityLogger } from '../../hooks/useActivityLogger';
import { Timestamp } from 'firebase/firestore';
import { formatSafeDate } from '../../utils/dateUtils';

const TasksPage: React.FC = () => {
  const { currentUser, userProfile } = useAuth();
  const { logActivity: logAct } = useActivityLogger();
  const [tasks, setTasks] = useState<Task[]>([]);
  const [stats, setStats] = useState<TaskStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTask, setEditingTask] = useState<Task | null>(null);
  const [filter, setFilter] = useState<'all' | 'pending' | 'overdue' | 'completed'>('pending');

  useEffect(() => {
    if (currentUser) {
      loadTasks();
    }
  }, [currentUser, filter]);

  const loadTasks = async () => {
    if (!currentUser) return;

    try {
      setLoading(true);
      const [userTasks, taskStats] = await Promise.all([
        taskService.getUserTasks(currentUser.uid, filter === 'completed'),
        taskService.getTaskStats({ userId: currentUser.uid })
      ]);

      let filteredTasks = userTasks;
      if (filter === 'overdue') {
        const now = new Date();
        filteredTasks = userTasks.filter(t =>
          t.status !== 'completed' && (t.dueDate?.toDate?.() || new Date()) < now
        );
      } else if (filter === 'completed') {
        filteredTasks = userTasks.filter(t => t.status === 'completed');
      } else if (filter === 'pending') {
        filteredTasks = userTasks.filter(t => t.status !== 'completed');
      }

      setTasks(filteredTasks);
      setStats(taskStats);
    } catch (error) {
      console.error('Error loading tasks:', error);
      toast.error('Failed to load tasks');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteTask = async (taskId: string) => {
    try {
      const task = tasks.find(t => t.id === taskId);
      if (task?.recurrence) {
        // Use the recurring task completion method
        const { nextTaskId } = await taskService.completeRecurringTask(taskId);
        if (nextTaskId) {
          toast.success('Task completed! Next occurrence created.');
        } else {
          toast.success('Recurring task series completed!');
        }
      } else {
        await taskService.updateTaskStatus(taskId, 'completed');
        toast.success('Task completed!');
      }
      logAct({ action: 'task_completed', entityType: 'task', entityId: taskId, entityName: task?.title });
      loadTasks();
    } catch (error) {
      console.error('Error completing task:', error);
      toast.error('Failed to complete task');
    }
  };

  const handleDeleteTask = async (taskId: string) => {
    if (!window.confirm('Are you sure you want to delete this task?')) return;

    try {
      await taskService.deleteTask(taskId);
      toast.success('Task deleted');
      loadTasks();
    } catch (error) {
      console.error('Error deleting task:', error);
      toast.error('Failed to delete task');
    }
  };

  const getPriorityColor = (priority: TaskPriority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-700 border-red-200';
      case 'high': return 'bg-orange-100 text-orange-700 border-orange-200';
      case 'medium': return 'bg-yellow-100 text-yellow-700 border-yellow-200';
      default: return 'bg-gray-100 text-gray-700 border-gray-200';
    }
  };

  const getTypeColor = (type: TaskType) => {
    switch (type) {
      case 'lead_followup': return 'bg-secondary-100 text-secondary-700';
      case 'document_upload': return 'bg-coral-100 text-coral-600';
      case 'training': return 'bg-accent-100 text-accent-700';
      case 'call': return 'bg-blue-100 text-blue-700';
      case 'meeting': return 'bg-purple-100 text-purple-700';
      case 'contract_signature': return 'bg-purple-100 text-purple-700';
      case 'consultation_request': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 py-6">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Tasks</h1>
            <p className="text-gray-600">Manage your tasks and follow-ups</p>
          </div>
          <button
            onClick={() => setShowCreateModal(true)}
            className="px-4 py-2 bg-gradient-to-r from-primary-500 to-primary-600 text-white rounded-xl hover:from-primary-600 hover:to-primary-700 shadow-md flex items-center gap-2"
          >
            <Plus className="h-5 w-5" />
            New Task
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-secondary-100 rounded-lg">
                  <Clock className="h-5 w-5 text-secondary-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.dueToday}</p>
                  <p className="text-sm text-gray-500">Due Today</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-red-100 rounded-lg">
                  <AlertTriangle className="h-5 w-5 text-red-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-red-600">{stats.overdue}</p>
                  <p className="text-sm text-gray-500">Overdue</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-primary-100 rounded-lg">
                  <Calendar className="h-5 w-5 text-primary-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-gray-900">{stats.dueThisWeek}</p>
                  <p className="text-sm text-gray-500">This Week</p>
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-accent-100 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-accent-600" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-accent-600">{stats.completed}</p>
                  <p className="text-sm text-gray-500">Completed</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Filter Tabs */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 mb-6">
          <div className="flex border-b border-gray-100">
            {(['all', 'pending', 'overdue', 'completed'] as const).map((f) => (
              <button
                key={f}
                onClick={() => setFilter(f)}
                className={`flex-1 py-3 text-sm font-medium transition-colors ${
                  filter === f
                    ? 'text-primary-600 border-b-2 border-primary-600'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {f.charAt(0).toUpperCase() + f.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* Task List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
          {loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto"></div>
            </div>
          ) : tasks.length === 0 ? (
            <div className="p-8 text-center">
              <CheckCircle className="h-12 w-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">No tasks found</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-50">
              {tasks.map((task) => {
                const isOverdue = (task.dueDate?.toDate?.() || new Date()) < new Date() && task.status !== 'completed';

                return (
                  <div
                    key={task.id}
                    className={`p-4 hover:bg-gray-50 transition-colors ${
                      task.status === 'completed' ? 'opacity-60' : ''
                    }`}
                  >
                    <div className="flex items-start gap-4">
                      <button
                        onClick={() => handleCompleteTask(task.id)}
                        disabled={task.status === 'completed'}
                        className={`mt-1 w-6 h-6 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-colors ${
                          task.status === 'completed'
                            ? 'bg-accent-500 border-accent-500 text-white'
                            : 'border-gray-300 hover:border-accent-500 hover:bg-accent-50'
                        }`}
                      >
                        {task.status === 'completed' && <CheckCircle className="h-4 w-4" />}
                      </button>

                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className={`font-medium ${task.status === 'completed' ? 'line-through text-gray-500' : 'text-gray-900'}`}>
                            {task.title}
                          </h3>
                          <span className={`px-2 py-0.5 rounded-full text-xs font-medium border ${getPriorityColor(task.priority)}`}>
                            {task.priority}
                          </span>
                          {task.autoGenerated && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-gray-100 text-gray-600">
                              Auto
                            </span>
                          )}
                          {task.recurrence && (
                            <span className="px-2 py-0.5 rounded-full text-xs bg-blue-100 text-blue-600 flex items-center gap-1">
                              <Repeat className="h-3 w-3" />
                              {task.recurrence.frequency === 'daily' && task.recurrence.interval === 1 ? 'Daily' :
                               task.recurrence.frequency === 'weekly' && task.recurrence.interval === 1 ? 'Weekly' :
                               task.recurrence.frequency === 'monthly' && task.recurrence.interval === 1 ? 'Monthly' :
                               `Every ${task.recurrence.interval} ${task.recurrence.frequency.replace('ly', '')}${task.recurrence.interval > 1 ? 's' : ''}`}
                            </span>
                          )}
                        </div>

                        {task.description && (
                          <p className="text-sm text-gray-500 mb-2">{task.description}</p>
                        )}

                        <div className="flex items-center gap-4 text-sm">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${getTypeColor(task.type)}`}>
                            {task.type.replace(/_/g, ' ')}
                          </span>

                          <span className={`flex items-center gap-1 ${isOverdue ? 'text-red-600 font-medium' : 'text-gray-500'}`}>
                            <Clock className="h-4 w-4" />
                            {isOverdue ? 'Overdue: ' : ''}
                            {formatSafeDate(task.dueDate, 'MMM d, h:mm a')}
                          </span>

                          {task.projectName && (
                            <span className="text-gray-500">
                              {task.projectName}
                            </span>
                          )}
                        </div>
                      </div>

                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setEditingTask(task)}
                          className="p-2 text-gray-400 hover:text-primary-500 hover:bg-primary-50 rounded-lg"
                          title="Edit task"
                        >
                          <Edit2 className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleDeleteTask(task.id)}
                          className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-lg"
                          title="Delete task"
                        >
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Create Task Modal */}
        {showCreateModal && (
          <CreateTaskModal
            onClose={() => setShowCreateModal(false)}
            onCreated={() => {
              setShowCreateModal(false);
              loadTasks();
            }}
          />
        )}

        {/* Edit Task Modal */}
        {editingTask && (
          <EditTaskModal
            task={editingTask}
            onClose={() => setEditingTask(null)}
            onUpdated={() => {
              setEditingTask(null);
              loadTasks();
            }}
          />
        )}
      </div>
    </div>
  );
};

// Create Task Modal Component
const CreateTaskModal: React.FC<{
  onClose: () => void;
  onCreated: () => void;
}> = ({ onClose, onCreated }) => {
  const { currentUser, userProfile } = useAuth();
  const { logActivity: logAct } = useActivityLogger();
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState<UserType[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: 'general' as TaskType,
    priority: 'medium' as TaskPriority,
    dueDate: format(new Date(), "yyyy-MM-dd'T'HH:mm"),
    assignedTo: currentUser?.uid || '',
    assignedToName: userProfile?.name || '',
    isRecurring: false,
    recurrenceFrequency: 'weekly' as 'daily' | 'weekly' | 'monthly',
    recurrenceInterval: 1,
    recurrenceEndDate: ''
  });

  useEffect(() => {
    loadUsers();
  }, []);

  useEffect(() => {
    // Set default assignee once users and currentUser are loaded
    if (currentUser && userProfile && !formData.assignedTo) {
      setFormData(prev => ({
        ...prev,
        assignedTo: currentUser.uid,
        assignedToName: userProfile.name
      }));
    }
  }, [currentUser, userProfile]);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      // Fetch users based on organization or all users for providers
      const fetchedUsers = await userService.getUsers();
      setUsers(fetchedUsers.filter(u => u.isActive !== false));
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserChange = (userId: string) => {
    const selectedUser = users.find(u => u.uid === userId);
    setFormData({
      ...formData,
      assignedTo: userId,
      assignedToName: selectedUser?.name || ''
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !userProfile) return;

    setSaving(true);
    try {
      const taskData: any = {
        organizationId: userProfile.organizationId || '',
        title: formData.title,
        description: formData.description,
        type: formData.type,
        priority: formData.priority,
        status: 'pending',
        assignedTo: formData.assignedTo,
        assignedToName: formData.assignedToName,
        assignedBy: currentUser.uid,
        assignedByName: userProfile.name,
        dueDate: Timestamp.fromDate(new Date(formData.dueDate)),
        autoGenerated: false,
        source: 'manual'
      };

      // Add recurrence if enabled
      if (formData.isRecurring) {
        taskData.recurrence = {
          frequency: formData.recurrenceFrequency,
          interval: formData.recurrenceInterval,
          ...(formData.recurrenceEndDate && {
            endDate: Timestamp.fromDate(new Date(formData.recurrenceEndDate + 'T23:59:59'))
          })
        };
      }

      await taskService.createTask(taskData);
      logAct({ action: 'task_created', entityType: 'task', entityName: formData.title });

      toast.success(formData.isRecurring ? 'Recurring task created successfully' : 'Task created successfully');
      onCreated();
    } catch (error) {
      console.error('Error creating task:', error);
      toast.error('Failed to create task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Create Task</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Task title..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              rows={3}
              placeholder="Optional description..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
            <select
              value={formData.assignedTo}
              onChange={(e) => handleUserChange(e.target.value)}
              disabled={loadingUsers}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {loadingUsers ? (
                <option>Loading users...</option>
              ) : (
                users.map(user => (
                  <option key={user.uid} value={user.uid}>
                    {user.name} {user.uid === currentUser?.uid ? '(Me)' : ''}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as TaskType })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="general">General</option>
                <option value="lead_followup">Lead Follow-up</option>
                <option value="call">Call</option>
                <option value="meeting">Meeting</option>
                <option value="document_upload">Document</option>
                <option value="review">Review</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as TaskPriority })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
            <input
              type="datetime-local"
              value={formData.dueDate}
              onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          {/* Recurrence Options */}
          <div className="border-t border-gray-100 pt-4">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.isRecurring}
                onChange={(e) => setFormData({ ...formData, isRecurring: e.target.checked })}
                className="w-4 h-4 text-primary-600 border-gray-300 rounded focus:ring-primary-500"
              />
              <span className="text-sm font-medium text-gray-700 flex items-center gap-1">
                <Repeat className="h-4 w-4" />
                Make this a recurring task
              </span>
            </label>

            {formData.isRecurring && (
              <div className="mt-3 pl-6 space-y-3">
                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Repeat every</span>
                  <input
                    type="number"
                    min="1"
                    max="99"
                    value={formData.recurrenceInterval}
                    onChange={(e) => setFormData({ ...formData, recurrenceInterval: parseInt(e.target.value) || 1 })}
                    className="w-16 px-2 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-center"
                  />
                  <select
                    value={formData.recurrenceFrequency}
                    onChange={(e) => setFormData({ ...formData, recurrenceFrequency: e.target.value as 'daily' | 'weekly' | 'monthly' })}
                    className="px-3 py-1 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="daily">{formData.recurrenceInterval === 1 ? 'day' : 'days'}</option>
                    <option value="weekly">{formData.recurrenceInterval === 1 ? 'week' : 'weeks'}</option>
                    <option value="monthly">{formData.recurrenceInterval === 1 ? 'month' : 'months'}</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm text-gray-600 mb-1">End date (optional)</label>
                  <input
                    type="date"
                    value={formData.recurrenceEndDate}
                    onChange={(e) => setFormData({ ...formData, recurrenceEndDate: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  />
                </div>
              </div>
            )}
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.title || !formData.assignedTo}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Creating...' : 'Create Task'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Edit Task Modal Component
const EditTaskModal: React.FC<{
  task: Task;
  onClose: () => void;
  onUpdated: () => void;
}> = ({ task, onClose, onUpdated }) => {
  const { currentUser, userProfile } = useAuth();
  const [saving, setSaving] = useState(false);
  const [users, setUsers] = useState<UserType[]>([]);
  const [loadingUsers, setLoadingUsers] = useState(true);

  // Format the existing dueDate for the datetime-local input
  const formatDateForInput = (timestamp: Timestamp) => {
    const date = timestamp.toDate();
    return format(date, "yyyy-MM-dd'T'HH:mm");
  };

  const [formData, setFormData] = useState({
    title: task.title,
    description: task.description || '',
    type: task.type,
    priority: task.priority,
    dueDate: formatDateForInput(task.dueDate),
    assignedTo: task.assignedTo,
    assignedToName: task.assignedToName
  });

  useEffect(() => {
    loadUsers();
  }, []);

  const loadUsers = async () => {
    try {
      setLoadingUsers(true);
      const fetchedUsers = await userService.getUsers();
      setUsers(fetchedUsers.filter(u => u.isActive !== false));
    } catch (error) {
      console.error('Error loading users:', error);
    } finally {
      setLoadingUsers(false);
    }
  };

  const handleUserChange = (userId: string) => {
    const selectedUser = users.find(u => u.uid === userId);
    setFormData({
      ...formData,
      assignedTo: userId,
      assignedToName: selectedUser?.name || ''
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser || !userProfile) return;

    setSaving(true);
    try {
      await taskService.updateTask(task.id, {
        title: formData.title,
        description: formData.description,
        type: formData.type,
        priority: formData.priority,
        assignedTo: formData.assignedTo,
        assignedToName: formData.assignedToName,
        dueDate: Timestamp.fromDate(new Date(formData.dueDate))
      });

      toast.success('Task updated successfully');
      onUpdated();
    } catch (error) {
      console.error('Error updating task:', error);
      toast.error('Failed to update task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full mx-4">
        <div className="p-4 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-lg font-semibold">Edit Task</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              placeholder="Task title..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              rows={3}
              placeholder="Optional description..."
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Assign To</label>
            <select
              value={formData.assignedTo}
              onChange={(e) => handleUserChange(e.target.value)}
              disabled={loadingUsers}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            >
              {loadingUsers ? (
                <option>Loading users...</option>
              ) : (
                users.map(user => (
                  <option key={user.uid} value={user.uid}>
                    {user.name} {user.uid === currentUser?.uid ? '(Me)' : ''}
                  </option>
                ))
              )}
            </select>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value as TaskType })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="general">General</option>
                <option value="lead_followup">Lead Follow-up</option>
                <option value="call">Call</option>
                <option value="meeting">Meeting</option>
                <option value="document_upload">Document</option>
                <option value="review">Review</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Priority</label>
              <select
                value={formData.priority}
                onChange={(e) => setFormData({ ...formData, priority: e.target.value as TaskPriority })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              >
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="urgent">Urgent</option>
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Due Date</label>
            <input
              type="datetime-local"
              value={formData.dueDate}
              onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
              required
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving || !formData.title || !formData.assignedTo}
              className="flex-1 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
            >
              {saving ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default TasksPage;
